import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  MenuItem,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash-es";
import SearchIcon from "@mui/icons-material/Search";
import ToolbarSelect from "components/Common/ToolbarSelect";
import DateRangePicker from "components/Common/DateRangePicker";
import { ZnsSendingMode } from "../types/znsCampaign";

const SEARCH_DEBOUNCE_MS = 400;

export interface AutoSendingReportFilters {
  searchTerm: string;
  znsCampaignName: string;
  znsCampaignId: string;
  znsTemplateId: string;
  templateId: string;
  /** Empty string = all */
  sendingMode: "" | ZnsSendingMode;
  /** API IsCurrent; "all" = omit */
  isCurrent: "all" | "true" | "false";
  activeFromRange: { from: string; to: string };
  activeToRange: { from: string; to: string };
  createdOnRange: { from: string; to: string };
}

interface AutoSendingReportToolbarProps {
  filters: AutoSendingReportFilters;
  onFilterChange: (filters: AutoSendingReportFilters) => void;
  onReset: () => void;
}

const SENDING_MODE_OPTIONS: { value: "" | ZnsSendingMode; label: string }[] = [
  { value: "", label: "All sending modes" },
  { value: ZnsSendingMode.IMMEDIATE, label: "Immediate" },
  { value: ZnsSendingMode.SCHEDULED, label: "Scheduled" },
  { value: ZnsSendingMode.EVENT_TRIGGERED, label: "Event triggered" },
  { value: ZnsSendingMode.CRON_SCHEDULED, label: "Cron scheduled" },
];

const IS_CURRENT_OPTIONS: { value: "all" | "true" | "false"; label: string }[] =
  [
    { value: "all", label: "All" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

const AutoSendingReportToolbar: React.FC<AutoSendingReportToolbarProps> = ({
  filters,
  onFilterChange,
  onReset,
}) => {
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const [nameInput, setNameInput] = useState(filters.znsCampaignName);
  const [znsTemplateIdInput, setZnsTemplateIdInput] = useState(
    filters.znsTemplateId,
  );

  const filtersRef = useRef(filters);
  const onFilterChangeRef = useRef(onFilterChange);
  filtersRef.current = filters;
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    setSearchInput(filters.searchTerm);
  }, [filters.searchTerm]);

  useEffect(() => {
    setNameInput(filters.znsCampaignName);
  }, [filters.znsCampaignName]);

  useEffect(() => {
    setZnsTemplateIdInput(filters.znsTemplateId);
  }, [filters.znsTemplateId]);

  const debouncedSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChangeRef.current({
          ...filtersRef.current,
          searchTerm: value,
        });
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  const debouncedCampaignName = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChangeRef.current({
          ...filtersRef.current,
          znsCampaignName: value,
        });
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  const debouncedZnsTemplateId = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChangeRef.current({
          ...filtersRef.current,
          znsTemplateId: value,
        });
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(
    () => () => {
      debouncedSearchTerm.cancel();
      debouncedCampaignName.cancel();
      debouncedZnsTemplateId.cancel();
    },
    [debouncedSearchTerm, debouncedCampaignName, debouncedZnsTemplateId],
  );

  const handleChange = (field: keyof AutoSendingReportFilters, value: any) => {
    onFilterChange({ ...filters, [field]: value });
  };

  // const handleSearchTermChange = useCallback(
  //   (value: string) => {
  //     setSearchInput(value);
  //     debouncedSearchTerm(value);
  //   },
  //   [debouncedSearchTerm],
  // );

  const handleCampaignNameChange = useCallback(
    (value: string) => {
      setNameInput(value);
      debouncedCampaignName(value);
    },
    [debouncedCampaignName],
  );

  const handleZnsTemplateIdChange = useCallback(
    (value: string) => {
      setZnsTemplateIdInput(value);
      debouncedZnsTemplateId(value);
    },
    [debouncedZnsTemplateId],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={1} alignItems="center">
        {/* <Grid item>
          <TextField
            label="Search"
            size="small"
            value={searchInput}
            onChange={(e) => handleSearchTermChange(e.target.value)}
            sx={{ minWidth: 200 }}
            placeholder="SearchTerm"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid> */}
        <Grid item>
          <TextField
            label="ZNS campaign name"
            size="small"
            value={nameInput}
            onChange={(e) => handleCampaignNameChange(e.target.value)}
            sx={{ minWidth: 200 }}
            InputProps={{}}
          />
        </Grid>
        <Grid item>
          <TextField
            label="ZNS campaign ID"
            size="small"
            value={filters.znsCampaignId}
            onChange={(e) => handleChange("znsCampaignId", e.target.value)}
            sx={{ minWidth: 180 }}
          />
        </Grid>
        <Grid item>
          <TextField
            label="ZNS template ID"
            size="small"
            value={znsTemplateIdInput}
            onChange={(e) => handleZnsTemplateIdChange(e.target.value)}
            sx={{ minWidth: 160 }}
          />
        </Grid>
        <Grid item>
          <TextField
            label="Template ID"
            size="small"
            value={filters.templateId}
            onChange={(e) => handleChange("templateId", e.target.value)}
            sx={{ minWidth: 120 }}
          />
        </Grid>
        <Grid item>
          <ToolbarSelect
            label="Is current"
            value={filters.isCurrent}
            onChange={(v) =>
              handleChange("isCurrent", v as "all" | "true" | "false")
            }
          >
            {IS_CURRENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </ToolbarSelect>
        </Grid>
        <Grid item>
          <DateRangePicker
            label="Active from"
            value={filters.activeFromRange}
            onChange={(from, to) =>
              handleChange("activeFromRange", { from, to })
            }
            format="DD/MM/YYYY"
            size="small"
            minWidth={220}
          />
        </Grid>
        <Grid item>
          <DateRangePicker
            label="Active to"
            value={filters.activeToRange}
            onChange={(from, to) => handleChange("activeToRange", { from, to })}
            format="DD/MM/YYYY"
            size="small"
            minWidth={220}
          />
        </Grid>
        <Grid item>
          <DateRangePicker
            label="Created on"
            value={filters.createdOnRange}
            onChange={(from, to) =>
              handleChange("createdOnRange", { from, to })
            }
            format="DD/MM/YYYY"
            size="small"
            minWidth={220}
          />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={onReset}
            sx={{
              height: 40,
              borderColor: "grey.400",
              color: "text.primary",
              fontWeight: "bold",
              ml: 1,
            }}
          >
            RESET
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(AutoSendingReportToolbar);
